import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { LoggerService } from '../logger/logger.service';
import { FEATURE_FLAGS } from './feature-flags.config';

const STORAGE_KEY = 'romulus-flags';

@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private readonly _flags$: BehaviorSubject<Record<string, boolean>>;
  private readonly logger = inject(LoggerService);

  public readonly flags$: Observable<Record<string, boolean>>;

  public constructor() {
    this._flags$ = new BehaviorSubject<Record<string, boolean>>(this.loadFlags());
    this.flags$ = this._flags$.asObservable();
  }

  public enabled(key: string): boolean {
    return this._flags$.value[key] ?? false;
  }

  public set(key: string, value: boolean): void {
    this.logger.debug(`Setting "${key}" as "${value}"`, 'FeatureFlagsService');
    const updated = { ...this._flags$.value, [key]: value };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    this._flags$.next(updated);
  }

  public reset(): void {
    this.logger.debug('Resetting flags', 'FeatureFlagsService');
    sessionStorage.removeItem(STORAGE_KEY);
    this._flags$.next(this.buildDefaults());
  }

  private loadFlags(): Record<string, boolean> {
    this.logger.debug('Loading feature flags from session storage', 'FeatureFlagsService');
    const defaults = this.buildDefaults();
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...defaults, ...JSON.parse(stored) };
      }
    } catch {
      this.logger.error('Failed to parse feature flags from session storage, reset to defaults', 'FeatureFlagsService');
    }

    return defaults;
  }

  private buildDefaults(): Record<string, boolean> {
    return Object.fromEntries(FEATURE_FLAGS.map((f) => [f.key, f.defaultValue]));
  }
}
